import { Text, Card, Group, Button, Space, Anchor } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconCalendar, IconFlame } from "@tabler/icons-react";
import { MouseEvent, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import campanhaRegulamentoIdeiasSchema from "@/modules/campanha-regulamento-ideias/schema";
import useUserStore from "@/modules/users/store";
import { useThemeTokens } from "@/theme/useThemeTokens";
import GetCampanhas from "./useGetCampanha";
import useGetRegulamentos from "./useGetRegulamentos";

const MAX_TITLE_SIZE = 100;
const CARD_HEIGHT = 380;

const Background = ({ children, color, isMobile }: { children: React.ReactNode; color: string; isMobile: boolean }) => {
  return (
    <Card
      withBorder
      p="md"
      bg={color}
      radius="md"
      w={isMobile ? "100%" : 250}
      miw={isMobile ? "100%" : 250}
      h={isMobile ? "auto" : CARD_HEIGHT}
      mih={CARD_HEIGHT}
      style={{ display: "flex", flexDirection: "column" }}
    >
      {children}
    </Card>
  );
};

const CampanhaDeInovacao = () => {
  const [reload, setReload] = useState(false);
  const [campanhas, setCampanhas] = useState<TCampanhaRegulamentoIdeias[]>([]);

  const { data } = useGetRegulamentos();
  const { activeEmpresaId, colaborador } = useUserStore();
  const { colors } = useThemeTokens();
  const color = colors.primary1;
  const isMobile = useMediaQuery("(max-width: 48em)") ?? false;
  const uniqueData = campanhas && campanhas[0];

  useEffect(() => {
    const fetchData = async () => {
      try {
        await loadData();
      } catch (error) {
        console.error("Ocorreu um erro ao carregar os dados:", error);
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchData();
  }, [data && data.length > 0, activeEmpresaId, reload]);

  const loadData = async () => {
    const campanha = await GetCampanhas(data, activeEmpresaId);

    const dataAtual = new Date();
    const filteredArray = campanha.filter((item) => {
      const dataInicial = new Date(item.dataInicial);
      const dataFinal = new Date(item.dataFinal);

      return dataInicial <= dataAtual && dataFinal >= dataAtual;
    });

    setCampanhas(filteredArray);
  };

  /**
   * Função que trata o clique do botão.
   * @param event - O evento de clique do botão.
   */
  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    const schema = campanhaRegulamentoIdeiasSchema;
    const defaultValues = schema.getDefault() as unknown;
    event.preventDefault();
    modals.openContextModal({
      size: "xl",
      innerProps: {
        item: schema.cast(defaultValues, { stripUnknown: true, assert: false }),
        data: campanhas,
        setReload: () => setReload((reload) => !reload)
      },
      modal: "modal-campanha"
    });
  }

  const hasCampanha = !!campanhas?.length;
  const hasMultipleCampanhas = hasCampanha && campanhas.length > 1;
  const canCreateCampanha = colaborador?.role !== "COLLABORATOR";

  if (hasMultipleCampanhas) {
    return (
      <Background color={color} isMobile={isMobile}>
        <Text c={colors.textStrong} fw={700} size="md">
          Visualize as campanhas de ideias
        </Text>
        <Space h="md" />
        <Group justify={"center"} mt="auto" pb="sm">
          <Button bg={color} onClick={handleClick}>
            Visualizar
          </Button>
        </Group>
      </Background>
    );
  }

  if (hasCampanha) {
    return (
      <Background color={color} isMobile={isMobile}>
        <Text c={colors.textStrong} fw={700} size="md">
          Campanha de fomento de ideias
        </Text>
        <Space h="md" />
        <Group justify={"flex-start"} align={"center"} style={{ gap: "unset", marginRight: 5 }}>
          <IconFlame color={colors.textStrong} style={{ fontSize: "12" }} />
          <Text c={colors.textStrong} fw={500} size="xs">
            Objetivo da campanha
          </Text>
        </Group>
        <Text c={colors.textStrong} fw={600} size="sm">
          {uniqueData && uniqueData?.descricao.length > MAX_TITLE_SIZE
            ? `${uniqueData?.descricao.slice(0, MAX_TITLE_SIZE)}...`
            : uniqueData?.descricao}
        </Text>
        <Space h="md" />
        <Group justify={"flex-start"} align={"center"} style={{ gap: "unset" }}>
          <IconCalendar color={colors.textStrong} style={{ fontSize: "12", marginRight: 5 }} />
          <Text c={colors.textStrong} fw={500} size="xs">
            Período de realização
          </Text>
        </Group>
        <Text c={colors.textStrong} fw={600} size="sm">
          {uniqueData?.dataInicial?.toLocaleDateString("pt-BR") ?? ""} -{" "}
          {uniqueData?.dataFinal?.toLocaleDateString("pt-BR") ?? ""}{" "}
        </Text>
        <Space h="md" />
        <Group justify={"center"} mt="auto" pb="sm">
          <Button bg={color} onClick={handleClick}>
            Entrar na campanha
          </Button>
        </Group>
      </Background>
    );
  }

  if (canCreateCampanha) {
    return (
      <Background color={color} isMobile={isMobile}>
        <Text c={colors.textStrong} fw={700} size="md">
          Cadastre uma campanha para iniciar:
        </Text>
        <Space h="xs" />
        <Text c={colors.textStrong} fw={400} size="xs">
          Acesse um regulamento existente e cadastre uma campanha de incentivo.
        </Text>
        <Space h="md" />
        <Group justify={"center"} mt="auto" pb="sm">
          <Anchor component={NavLink} c={color} to={`/ideias/regulamentos`} underline="never">
            <Button bg={colors.primary}>Cadastrar</Button>
          </Anchor>
        </Group>
      </Background>
    );
  }

  return <></>;
};

export default CampanhaDeInovacao;
